import { c as _c } from "react/compiler-runtime";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import Speed from "@mui/icons-material/Speed";
import { Box, Grid, IconButton, Input, Slider, Stack, Typography, useTheme } from "@mui/material";
import { selectCallstackIsPlaying, selectCallstackLength, selectIsLastFrame, selectIsRootFrame } from "#/features/callstack/model/callstackSlice";
import { useI18nContext } from "#/shared/hooks";
import { FrameIndexLabel } from "#/shared/ui/atoms/FrameIndexLabel";
import { iconButtonHoverSx } from "#/shared/ui/styles/iconButtonHoverStyles";
import { useAppSelector } from "#/store/hooks";
export type PlayerControlsProps = {
  disabled?: boolean;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  handleStepBack: () => void;
  handlePlay: () => void;
  handleReplay: () => void;
  handleStepForward: () => void;
};
export const PlayerControls: React.FC<PlayerControlsProps> = t0 => {
  const $ = _c(69);
  const {
    disabled,
    sliderValue,
    setSliderValue,
    handleStepBack,
    handlePlay,
    handleReplay,
    handleStepForward
  } = t0;
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const callstackLength = useAppSelector(selectCallstackLength);
  const isPlaying = useAppSelector(selectCallstackIsPlaying);
  const isRootFrame = useAppSelector(selectIsRootFrame);
  const isLastFrame = useAppSelector(selectIsLastFrame);
  let t1;
  if ($[0] !== setSliderValue) {
    t1 = (_, newValue) => {
      setSliderValue(newValue as number);
    };
    $[0] = setSliderValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSliderChange = t1;
  let t2;
  if ($[2] !== setSliderValue) {
    t2 = event => {
      const parsed = Number(event.target.value);
      if (!Number.isFinite(parsed)) {
        return;
      }
      setSliderValue(parsed);
    };
    $[2] = setSliderValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleInputChange = t2;
  let t3;
  if ($[4] !== setSliderValue || $[5] !== sliderValue) {
    t3 = () => {
      const clamped = Math.min(700, Math.max(1, sliderValue));
      if (sliderValue !== clamped) {
        setSliderValue(clamped);
      }
    };
    $[4] = setSliderValue;
    $[5] = sliderValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleBlur = t3;
  const canReplay = Boolean(callstackLength && !isPlaying && isLastFrame);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      py: 1,
      px: 2,
      alignItems: "center",
      justifyContent: "flex-end"
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: "100%"
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== LL) {
    t6 = LL.PLAYBACK_INTERVAL();
    $[9] = LL;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <Typography id="input-slider" variant="caption" gutterBottom={true}>{t6}</Typography>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      alignItems: "center"
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Grid><Speed /></Grid>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== disabled || $[16] !== handleSliderChange || $[17] !== sliderValue) {
    t10 = <Grid size="grow"><Slider disabled={disabled} value={sliderValue} min={1} max={700} onChange={handleSliderChange} aria-labelledby="input-slider" /></Grid>;
    $[15] = disabled;
    $[16] = handleSliderChange;
    $[17] = sliderValue;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== LL) {
    t11 = () => <Typography variant="caption">{LL.MS()}</Typography>;
    $[19] = LL;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      step: 5,
      min: 1,
      max: 700,
      type: "number",
      "aria-labelledby": "input-slider"
    };
    t13 = {
      width: "64px"
    };
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  if ($[23] !== disabled || $[24] !== handleBlur || $[25] !== handleInputChange || $[26] !== sliderValue || $[27] !== t11) {
    t14 = <Grid><Input disabled={disabled} value={sliderValue} size="small" onChange={handleInputChange} onBlur={handleBlur} renderSuffix={t11} inputProps={t12} sx={t13} /></Grid>;
    $[23] = disabled;
    $[24] = handleBlur;
    $[25] = handleInputChange;
    $[26] = sliderValue;
    $[27] = t11;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t14) {
    t15 = <Grid container={true} spacing={2} sx={t8}>{t9}{t10}{t14}</Grid>;
    $[29] = t10;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t7) {
    t16 = <Box sx={t5}>{t7}{t15}</Box>;
    $[32] = t15;
    $[33] = t7;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      position: "relative",
      alignItems: "center"
    };
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = disabled || !callstackLength || isRootFrame;
  let t19;
  if ($[36] !== theme) {
    t19 = iconButtonHoverSx(theme);
    $[36] = theme;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FirstPage />;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== handleStepBack || $[40] !== t18 || $[41] !== t19) {
    t21 = <IconButton title="Step back" disabled={t18} onClick={handleStepBack} sx={t19}>{t20}</IconButton>;
    $[39] = handleStepBack;
    $[40] = t18;
    $[41] = t19;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Box sx={{
      position: "absolute",
      bottom: -1,
      left: "47%",
      transform: "translate(-50%, 50%)"
    }}><FrameIndexLabel /></Box>;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = canReplay ? "Replay" : isPlaying ? "Pause" : "Play";
  const t24 = !callstackLength;
  const t25 = canReplay ? handleReplay : handlePlay;
  let t26;
  if ($[44] !== theme) {
    t26 = iconButtonHoverSx(theme);
    $[44] = theme;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== canReplay || $[47] !== isPlaying) {
    t27 = canReplay ? <Replay /> : isPlaying ? <Pause /> : <PlayArrow />;
    $[46] = canReplay;
    $[47] = isPlaying;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t23 || $[50] !== t24 || $[51] !== t25 || $[52] !== t26 || $[53] !== t27) {
    t28 = <IconButton title={t23} disabled={t24} onClick={t25} sx={t26}>{t27}</IconButton>;
    $[49] = t23;
    $[50] = t24;
    $[51] = t25;
    $[52] = t26;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  const t29 = disabled || !callstackLength || isLastFrame;
  let t30;
  if ($[55] !== theme) {
    t30 = iconButtonHoverSx(theme);
    $[55] = theme;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <LastPage />;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== handleStepForward || $[59] !== t29 || $[60] !== t30) {
    t32 = <IconButton title="Step forward" disabled={t29} onClick={handleStepForward} sx={t30}>{t31}</IconButton>;
    $[58] = handleStepForward;
    $[59] = t29;
    $[60] = t30;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t21 || $[63] !== t28 || $[64] !== t32) {
    t33 = <Stack direction="row" spacing={0.5} sx={t17}>{t21}{t22}{t28}{t32}</Stack>;
    $[62] = t21;
    $[63] = t28;
    $[64] = t32;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t16 || $[67] !== t33) {
    t34 = <Stack direction="row" spacing={1} onMouseDown={_temp} sx={t4}>{t16}{t33}</Stack>;
    $[66] = t16;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  return t34;
};
function _temp(event_0) {
  return event_0.stopPropagation();
}